import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AuthenticationError, ResponseError } from '@backstage/errors';
import { FetchOpts, Group, DevPortalOrgHubApi, ApiResponse, User } from './types';

const DEFAULT_PROXY_PATH = '/devportal-org-hub';
const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export class DevPortalOrgHubClient implements DevPortalOrgHubApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly proxyPath: string;
  private readonly fetchApi: FetchApi;

  static fromConfig(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    return new DevPortalOrgHubClient({
      fetchApi,
      discoveryApi,
      proxyPath: DEFAULT_PROXY_PATH,
    });
  }

  constructor(opts: { discoveryApi: DiscoveryApi; fetchApi: FetchApi; proxyPath: string }) {
    this.discoveryApi = opts.discoveryApi;
    this.proxyPath = opts.proxyPath;
    this.fetchApi = opts.fetchApi;
  }

  private async apiUrl() {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return proxyUrl + this.proxyPath;
  }

  private async fetch<T = any>(input: string, init?: RequestInit): Promise<T> {
    const apiUrl = await this.apiUrl();

    const response = await this.fetchApi.fetch(`${apiUrl}${input}`, init);
    if (response.status === 401) {
      throw new AuthenticationError('This request requires HTTP authentication.');
    }
    if (!response.ok || response.status >= 400) {
      throw await ResponseError.fromResponse(response);
    }

    return await response.json();
  }

  async fetchEntities<T>(endpoint: string, opts?: FetchOpts | undefined): Promise<T[]> {
    const init = {
      headers: JSON_HEADERS,
    };

    const { limit, offset } = opts ?? {};
    const query = new URLSearchParams();
    if (limit) {
      query.append('limit', String(limit));
    }
    if (offset) {
      query.append('offset', String(offset));
    }

    const { data } = await this.fetch<ApiResponse<T>>(`${endpoint}?${query.toString()}`, init);
    return data;
  }

  async fetchGroups(opts?: FetchOpts | undefined): Promise<Group[]> {
    return this.fetchEntities<Group>('/groups', opts);
  }

  async fetchUsers(opts?: FetchOpts | undefined): Promise<User[]> {
    return this.fetchEntities<User>('/users', opts);
  }
}
